/**
 * LoR Template Selector
 *
 * Presentational component that lists available LoR document templates and
 * allows the user to choose one. It is intentionally stateless; selection
 * state is controlled by the parent.
 */
import React from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TemplateCategory } from '@/services/api/generated/templatesV2Service';
import { TEMPLATE_CATEGORY_DISPLAY_NAMES } from '@/services/types/letters-types';

import type { TemplateLite } from '../types';

type Props = {
  templates: TemplateLite[];
  selectedTemplateId: string | null;
  isLoading: boolean;
  onChange: (id: string) => void;
  filterActive?: boolean;
  filterLabel?: string | null;
};

/**
 * LorTemplateSelector
 *
 * Dropdown control for choosing a Letter of Representation (LoR) template.
 * Displays human-friendly template names and disables interaction while
 * loading or when no templates are present.
 *
 * Props
 * - `templates`: available LoR-capable templates
 * - `selectedTemplateId`: currently chosen template id (or null)
 * - `isLoading`: disables the control and shows a loading placeholder
 * - `onChange`: called with the newly selected template id
 */
export const LorTemplateSelector: React.FC<Props> = ({
  templates,
  selectedTemplateId,
  isLoading,
  onChange,
  filterActive = false,
  filterLabel,
}) => {
  const prettyTemplateLabel = (category: TemplateCategory): string =>
    TEMPLATE_CATEGORY_DISPLAY_NAMES[category] || 'Letter of Representation';

  const activeTemplate = templates.find((t) => t.id === selectedTemplateId);

  if (!isLoading && templates.length === 0) {
    // No templates available – show a lightweight inline notice instead of hiding.
    return (
      <div className="p-3">
        <div
          id="lor-template-label"
          className="block text-xs font-medium text-gray-700 mb-1"
        >
          Template
        </div>
        <div role="status" aria-live="polite" className="w-full px-0 py-0">
          <p className="text-sm text-gray-700">
            {filterActive && filterLabel
              ? `No LoR templates available for ${filterLabel}.`
              : 'No LoR templates available for this matter’s firm — unable to generate a Letter of Representation.'}
          </p>
          {filterActive && (
            <p className="text-xs text-gray-500 mt-1">
              Upload a template for this attorney or update the matter’s
              representing attorney to continue.
            </p>
          )}
        </div>
      </div>
    );
  }

  let selectorBody: React.ReactNode = null;
  if (isLoading) {
    selectorBody = (
      <Select
        value={selectedTemplateId || ''}
        onValueChange={onChange}
        disabled
      >
        <SelectTrigger
          className="w-full"
          aria-label="Loading templates"
          aria-labelledby="lor-template-label"
        >
          <SelectValue placeholder="Loading…" />
        </SelectTrigger>
      </Select>
    );
  } else {
    selectorBody = (
      <Select value={selectedTemplateId || ''} onValueChange={onChange}>
        <SelectTrigger
          className="w-full"
          aria-label="Select LoR template"
          aria-labelledby="lor-template-label"
        >
          <SelectValue placeholder="Select template" />
        </SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.id} value={t.id}>
              <div className="flex flex-col">
                <span>{t.name || prettyTemplateLabel(t.category)}</span>
                {t.attorney_name && (
                  <span className="text-xs text-gray-500">
                    Attorney: {t.attorney_name}
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <div className="p-3">
      <div
        id="lor-template-label"
        className="block text-xs font-medium text-gray-700 mb-1"
      >
        Template
      </div>
      {selectorBody}
      {filterActive && (
        <p className="text-xs text-blue-600 mt-2">
          Showing templates configured for{' '}
          {filterLabel || 'the representing attorney'}.
        </p>
      )}
      {activeTemplate?.attorney_name && (
        <p className="text-xs text-gray-600 mt-2">
          Attorney for this template: {activeTemplate.attorney_name}
        </p>
      )}
    </div>
  );
};
